/** Public opening frequencies. Only the board position is sent to Lichess. */
export interface PopularMove {
  uci: string;
  san: string;
  games: number;
}

/**
 * Lichess now answers anonymous explorer requests with 401. Once that happens the
 * lookup is skipped for the rest of the session, so browsing does not keep sending
 * positions to Lichess for requests that cannot succeed.
 */
let rejected = false;

export async function popularMoves(fen: string, signal?: AbortSignal): Promise<PopularMove[]> {
  // The mock SPA runs entirely offline in tests; real builds never use fixture data.
  if (import.meta.env.VITE_USE_MOCK === '1' || import.meta.env.MODE === 'test') return [];
  if (rejected) throw new Error('Lichess requires sign-in for opening frequencies.');

  const url = new URL('https://explorer.lichess.org/lichess');
  url.searchParams.set('variant', 'standard');
  url.searchParams.set('fen', fen);
  url.searchParams.set('speeds', 'blitz,rapid,classical');
  url.searchParams.set('ratings', '1000,1200,1400,1600,1800,2000,2200');
  url.searchParams.set('moves', '12');
  url.searchParams.set('topGames', '0');
  url.searchParams.set('recentGames', '0');

  const response = await fetch(url, { signal, credentials: 'omit' });
  if (response.status === 401 || response.status === 403) {
    rejected = true;
    throw new Error('Lichess requires sign-in for opening frequencies.');
  }
  if (!response.ok) throw new Error('Opening frequencies are temporarily unavailable.');
  const body: unknown = await response.json();
  if (typeof body !== 'object' || body === null || !('moves' in body) || !Array.isArray(body.moves)) {
    throw new Error('Opening frequencies were not understood.');
  }
  return body.moves.flatMap((move: unknown): PopularMove[] => {
    if (typeof move !== 'object' || move === null) return [];
    const candidate = move as Record<string, unknown>;
    const counts = [candidate.white, candidate.draws, candidate.black];
    if (
      typeof candidate.uci !== 'string' ||
      typeof candidate.san !== 'string' ||
      !counts.every((count) => typeof count === 'number' && Number.isFinite(count) && count >= 0)
    ) return [];
    return [{
      uci: candidate.uci,
      san: candidate.san,
      games: (counts as number[]).reduce((sum, count) => sum + count, 0),
    }];
  });
}
